import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import HelpDrawer from "./HelpDrawer";
import { getHelpContext, getHelpContextForPath } from "./helpContent";
import "./AiAssist.scss";
import CopilotDrawer from "./CopilotDrawer";
import { useOnboarding } from "../../hooks/useOnboarding";

export const COPILOT_ENABLED = false;

const ContextualHelpContext = createContext(null);

export const HelpProvider = ({ children }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isCopilotOpen, setIsCopilotOpen] = useState(false);
  const [registeredContextId, setRegisteredContextId] = useState(null);
  const contextId =
    registeredContextId || getHelpContextForPath(location.pathname);
  const context = useMemo(() => getHelpContext(contextId), [contextId]);
  const openHelp = useCallback(() => setIsOpen(true), []);
  const closeHelp = useCallback(() => setIsOpen(false), []);
  const openCopilot = useCallback(() => setIsCopilotOpen(true), []);
  const closeCopilot = useCallback(() => setIsCopilotOpen(false), []);
  const { launchTour, openGettingStarted } = useOnboarding();
  useEffect(() => {
    setRegisteredContextId(null);
  }, [location.pathname]);

  useEffect(() => {
    window.sessionStorage.removeItem("piers-contextual-help-pinned");
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeHelp();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeHelp]);

  const value = useMemo(
    () => ({
      context,
      contextId,
      isOpen,
      openHelp,
      closeHelp,
      isCopilotOpen,
      openCopilot,
      closeCopilot,
      registerContext: setRegisteredContextId,
    }),
    [
      context,
      contextId,
      isOpen,
      openHelp,
      closeHelp,
      isCopilotOpen,
      openCopilot,
      closeCopilot,
    ]
  );

  return (
    <ContextualHelpContext.Provider value={value}>
      {children}
      <HelpDrawer
        context={context}
        isOpen={isOpen}
        onClose={closeHelp}
        onPiersTour={launchTour}
        onGettingStarted={openGettingStarted}
      />
      {COPILOT_ENABLED && (
        <>
          <CopilotDrawer
            context={context}
            isOpen={isCopilotOpen}
            onClose={closeCopilot}
          />
          <button
            type="button"
            className="ai-assist-fab"
            aria-label="Open PIERS Copilot"
            title="PIERS Copilot"
            onClick={openCopilot}
          >
            <img src="/images/piers-copilot.png" alt="" />
            <span>PIERS Copilot</span>
          </button>
        </>
      )}
    </ContextualHelpContext.Provider>
  );
};

export const useContextualHelp = () => {
  const help = useContext(ContextualHelpContext);
  if (!help)
    throw new Error("useContextualHelp must be used within HelpProvider.");
  return help;
};

// Pages can opt into an explicit context without adding any drawer markup.
export const PageHelpContext = ({ id, children }) => {
  const { registerContext } = useContextualHelp();

  useEffect(() => {
    registerContext(id);
    return () => registerContext(null);
  }, [id, registerContext]);

  return children;
};
